import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { AbstractService } from '../commons/abstract.service';
import { AudienceCounts } from '../models/audience-summary.model';
import { Channel } from '../models/channel.model';
import { Contact } from '../models/contact.model';
import { ImportContacts } from '../models/import-contacts.model';
import { ViaCepRes } from '../models/viacep.model';
import { LeadSource } from '../models/lead-source.model';

@Injectable({ providedIn: 'root' })
export class ContactService extends AbstractService<Contact> {

  getURLBase(): string {
    return '/contact';
  }

  constructor(private http: HttpClient) {
    super(http);
  }

  preCreate(obj: Contact): void {
    delete obj?._id;
  }

  preUpdate(obj: Contact): void {
  }

  updateTags(obj: Contact): Observable<Contact> {
    return super.put(`/tags/${obj._id}`, obj);
  }

  updateLeadSource(contactId: string, leadSource: LeadSource): Observable<Contact> {
    return super.put(`/lead-source/${contactId}`, <LeadSource>{_id: leadSource._id});
  }

  removeLeadSource(contactId: string): Observable<Contact> {
    return super.put(`/lead-source/${contactId}/remove`, {});
  }

  searchCep(cep: string): Observable<ViaCepRes> {
    return this.http.get<ViaCepRes>(`/integrations/viacep/${cep}`);
  }

  count(ids?: string[]): Promise<any> {
    let params = {};
    if (ids) {
      params['id'] = ids;
    }
    return lastValueFrom(this.http.get<number>(`/contact/tags/count/`, { params }));
  }

  countContactGroup(ids?: string[]): Promise<any> {
    let params = {};
    if (ids) {
      params['id'] = ids;
    }
    return lastValueFrom(this.http.get<number>(`/contact/contactGroup/count/`, { params }));
  }

  audienceCounts(): Observable<AudienceCounts> {
    return super.get(`/audience/counts`);
  }

  audienceCountItem(kind: 'tag' | 'group', id: string): Observable<number> {
    return super.get(`/audience/counts/${kind}/${id}`);
  }

  importContacts(element: ImportContacts): Observable<any> {
    return this.http.post<any>(`/contact/import`, element);
  }

  getByBirthday(page: number, perPage: number): Observable<Contact[]> {
    return super.get(`/birthday?page=${page}&perPage=${perPage}`);
  }

  getFieldsCustom(idContact: string): Observable<Array<any>> {
    return super.get(`/custom/${idContact}`);
  }

  getTotalizerContact(queryParams?: any): Observable<Array<{ count: number, validStatus: string }>> {
    if (queryParams) {
      return super.getWithParams(`/totalizer`, queryParams);
    }
    return super.get(`/totalizer`);
  }

  /**
   * `setBlacklisted` and `setOutOfIcp` moved to `CustomerService` on 2026-08-21: the
   * markings are enrichment and enrichment lives on the CRM record. `blockContact`
   * below stays — that one blocks the contact on a channel and is an attendance action,
   * which is a different thing that merely shares a permission.
   */


  blockContact(contact: Contact, channel: Channel): Observable<void> {
    return super.post('/block', { contact, channel });
  }

  unblockContact(contact: Contact, channel: Channel): Observable<void> {
    return super.post('/unblock', { contact, channel });
  }

  getContactDetailsWithCounts(contactId: string): Observable<unknown> {
    return super.get(`/data/${contactId}`);
  }

  getIdUserWithPortfolio(): Observable<unknown> {
    return super.get(`/user-with-portfolio`);
  }

  removeFromCustomerPortfolio(idsContact: string[]): Observable<void> {
    return super.put(`/remove-customer-portfolio`, idsContact);
  }

  transferContactsCustomerPortfolio(idDepartment: string, idAttendant: string, idsContact: string[]): Observable<void> {
    return super.put(`/transfer-customer-portfolio`, { idDepartment, idAttendant, idsContact });
  }

  getCountTotalContact(idCompany: string): Observable<number> {
    return super.get(`/total/${idCompany}`);
  }
}
